import { Trans, useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import React, { useMemo, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import i18next from "i18next";
import { formatCurrency } from "../../../utils";
import useTemplate from "../../../hooks/useTemplate";
import { getTemplateTranslation } from "../../../Providers/TemplateInstanceProvider";
import { PRODUCT_DIGITAL_SET_UUID, PRODUCT_INVOICE_UUID, REBATE_CODE_UUID, usePriceList, } from "../../providers/PriceListProvider";
import { isServerProductOrderLine, ORDER_LINE_TYPE, } from "../../types/ShoppingCart";
import VoucherCodeDialog from "./VoucherCodeDialog";
import { getProductTitle } from "../../utils/orders";
const useStyles = makeStyles((theme) => ({
    orderLineDescription: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        flex: "1 1 100%",
        fontWeight: "500",
        fontSize: "14px",
    },
    orderLineSubHeader: {
        fontSize: "12px",
        marginBottom: theme.spacing(2),
    },
    orderLinePrice: {
        flex: "0 0 auto",
        textAlign: "right",
        fontWeight: "bold",
    },
    priceBeforeRebate: {
        flex: "0 0 auto",
        textAlign: "right",
        color: "#A5A5A5",
        textDecoration: "line-through",
    },
    orderContentList: {
        listStyle: "none",
        margin: 0,
        padding: 0,
        borderBottom: `1px solid ${theme.palette.primary.light}`,
        marginTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        "& > li": {
            marginBottom: theme.spacing(2),
        },
        "& > li:last-child": {
            marginBottom: 0,
        },
    },
    orderTotalPriceList: {
        paddingBottom: "2px",
    },
    orderLinePrimary: {
        fontSize: "15px",
        fontWeight: "bold",
    },
    orderLineSecondary: {
        fontStyle: "italic",
        fontSize: "13px",
    },
    packageProductList: {
        paddingTop: theme.spacing(1),
        paddingLeft: "20px",
        listStyle: "none",
        "& > li > ul": {
            paddingLeft: "20px",
            listStyle: "disc",
            marginBottom: theme.spacing(1),
            lineHeight: "1.2em",
        },
    },
    packageTemplateName: {
        fontSize: "12px",
    },
    packageProductName: {
        marginBottom: "4px",
        lineHeight: "1.05em",
    },
    packagePartListPrimary: {
        fontSize: "15px",
    },
    packagePartListSecondary: {
        fontSize: "13px",
        fontStyle: "italic",
    },
    subProductName: {
        fontSize: "12px",
        fontWeight: 500,
        fontStyle: "normal",
    },
    orderTotalPrice: {
        fontSize: "20px",
        fontWeight: "bold",
        paddingBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.primary.light}`,
    },
    priceToPay: {
        fontWeight: "bold",
    },
    addCodeButton: {
        color: theme.palette.primary.main,
        border: 0,
        backgroundColor: "transparent",
        cursor: "pointer",
        fontSize: "15px",
    },
    oldPrice: {
        textDecoration: "line-through",
        color: "#A5A5A5",
        fontWeight: "normal",
    },
}));
const OrderContent = ({ orderLines, hasActiveRebateCampaigns, rebateCode, }) => {
    const classes = useStyles();
    const productList = orderLines
        .filter(isServerProductOrderLine)
        .filter((ol) => ol.product.uuid !== PRODUCT_INVOICE_UUID);
    const summaryList = orderLines.filter((ol) => {
        var _a;
        return ol.type === ORDER_LINE_TYPE.DELIVERY ||
            ((_a = ol.product) === null || _a === void 0 ? void 0 : _a.uuid) === PRODUCT_INVOICE_UUID;
    });
    const totalPriceLine = orderLines.find((ol) => ol.type === ORDER_LINE_TYPE.TOTAL);
    return (React.createElement(React.Fragment, null,
        React.createElement("ul", { className: classes.orderContentList }, productList.map((orderLine) => {
            return (React.createElement("li", { key: orderLine.uuid }, "package" in orderLine ? (React.createElement(PackageInCart, { orderLine: orderLine })) : (React.createElement(ProductInCart, { orderLine: orderLine }))));
        })),
        summaryList.length > 0 && (React.createElement("ul", { className: classes.orderContentList }, summaryList.map((orderLine, index) => {
            return (React.createElement("li", { key: index },
                React.createElement(DisplayOrderSummary, { orderLine: orderLine })));
        }))),
        hasActiveRebateCampaigns && (React.createElement(VoucherCodeSection, { orderLines: orderLines, rebateCode: rebateCode })),
        totalPriceLine && (React.createElement("ul", { className: `${classes.orderContentList} ${classes.orderTotalPriceList}` },
            React.createElement("li", { className: classes.orderTotalPrice },
                React.createElement(Box, { display: "flex", justifyContent: "space-between" },
                    React.createElement("span", null, totalPriceLine.description),
                    React.createElement("span", null, formatCurrency(totalPriceLine.linePrice))))))));
};
const VoucherCodeSection = ({ orderLines, rebateCode, }) => {
    const rebateLines = orderLines
        .filter((ol) => {
        var _a;
        return ol.type === ORDER_LINE_TYPE.DISCOUNT ||
            (ol.type === ORDER_LINE_TYPE.ADDED_COST &&
                ((_a = ol.product) === null || _a === void 0 ? void 0 : _a.uuid) === REBATE_CODE_UUID);
    })
        .map((ol) => ({ description: ol.description, linePrice: ol.linePrice }));
    const classes = useStyles();
    const [voucherCodeDialogIsOpen, setVoucherCodeDialogIsOpen] = useState(false);
    const [t] = useTranslation();
    let content = (React.createElement("li", null,
        React.createElement(Box, { display: "flex", justifyContent: "space-between" },
            React.createElement("span", null,
                React.createElement(Trans, null, "Voucher code")),
            React.createElement("button", { className: classes.addCodeButton, onClick: () => setVoucherCodeDialogIsOpen(true) },
                React.createElement(Trans, null, "Add code")))));
    // Happens if we used an rebate code but didn't get any rebate:
    if (rebateCode && rebateLines.length == 0) {
        rebateLines.push({
            description: t("Discount"),
            linePrice: 0,
        });
    }
    if (rebateLines.length > 0) {
        content = (React.createElement(React.Fragment, null, rebateLines.map((rebateLine, index) => {
            return (React.createElement(Box, { key: index, display: "flex", justifyContent: "space-between" },
                React.createElement("span", null, rebateLine.description),
                React.createElement("span", { className: classes.priceToPay }, formatCurrency(rebateLine.linePrice))));
        })));
    }
    return (React.createElement("ul", { className: classes.orderContentList },
        content,
        voucherCodeDialogIsOpen && (React.createElement(VoucherCodeDialog, { handleClose: () => setVoucherCodeDialogIsOpen(false) }))));
};
const PackageInCart = ({ orderLine }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { display: "flex", justifyContent: "space-between" },
            React.createElement("div", { className: classes.orderLinePrimary },
                orderLine.quantity > 1
                    ? t("%d pcs", {
                        postProcess: "sprintf",
                        sprintf: [orderLine.quantity],
                    })
                    : null,
                " ",
                orderLine.description),
            React.createElement(Box, { className: orderLine.rebate
                    ? classes.priceBeforeRebate
                    : classes.orderLinePrice }, formatCurrency(orderLine.linePrice))),
        orderLine.rebate && (React.createElement(Box, { display: "flex", justifyContent: "space-between" },
            React.createElement("div", null,
                React.createElement(Trans, null, "Buy now price")),
            React.createElement(Box, { className: classes.orderLinePrice }, formatCurrency(orderLine.actualLinePrice)))),
        React.createElement(PackageItemList, { items: orderLine.package.packageItems })));
};
export const groupPackageItemsByProduct = (items) => {
    var _a;
    const products = new Map();
    for (const packageItem of items) {
        const { uuid, name } = packageItem.product;
        const product = (_a = products.get(uuid)) !== null && _a !== void 0 ? _a : { uuid, name, packageItems: [] };
        product.packageItems.push(packageItem);
        products.set(uuid, product);
    }
    return [...products.values()].sort((a, b) => a.name.localeCompare(b.name));
};
const PackageItemList = ({ items }) => {
    const classes = useStyles();
    const products = groupPackageItemsByProduct(items);
    return (React.createElement("ul", { className: classes.packageProductList }, products.map((product) => {
        return (React.createElement("li", { key: product.uuid },
            React.createElement("div", { className: classes.packageProductName }, product.name),
            React.createElement("ul", null, product.packageItems.map((packageItem, index) => {
                return (React.createElement("li", { key: index },
                    React.createElement("div", { className: classes.packageTemplateName }, getPackageItemDescription(packageItem))));
            }))));
    })));
};
export const getPackageItemDescription = (packageItem) => {
    switch (packageItem.type) {
        case "DIGITAL_SET":
        case "DIGITAL_FILE":
            return `${packageItem.subject.group}: ${packageItem.subject.name}`;
        case "CATALOGUE":
            return packageItem.catalogue.name;
        case "TEMPLATE_PRODUCT":
            return packageItem.template.name;
    }
};
export const TemplateName = ({ uuid }) => {
    const [template, isLoading] = useTemplate(uuid);
    if (isLoading || !template) {
        return null;
    }
    return React.createElement(React.Fragment, null, getTemplateTranslation(template.name, i18next.language));
};
const ProductInCart = ({ orderLine, }) => {
    var _a;
    const { t } = useTranslation();
    const classes = useStyles();
    const productTitle = getProductTitle(orderLine, t);
    return (React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
        React.createElement("div", null,
            React.createElement("div", { className: classes.orderLinePrimary },
                orderLine.quantity > 1
                    ? t("%d pcs", {
                        postProcess: "sprintf",
                        sprintf: [orderLine.quantity],
                    })
                    : null,
                " ",
                productTitle),
            React.createElement("div", { className: classes.orderLineSecondary },
                React.createElement(SubProducts, { productUuid: orderLine.product.uuid, subProductUuids: (_a = orderLine.subProductUuids) !== null && _a !== void 0 ? _a : [] }),
                React.createElement(OrderLineShortDescription, { orderLine: orderLine }))),
        React.createElement(Box, { className: classes.orderLinePrice },
            React.createElement(OrderLinePrice, { orderLine: orderLine }))));
};
const OrderLinePrice = ({ orderLine, }) => {
    const classes = useStyles();
    if (orderLine.rebate) {
        return (React.createElement("div", null,
            React.createElement("div", { className: classes.oldPrice }, formatCurrency(orderLine.linePrice)),
            React.createElement("div", null, formatCurrency(orderLine.actualLinePrice))));
    }
    else {
        return React.createElement("div", null, formatCurrency(orderLine.actualLinePrice));
    }
};
export const OrderLineShortDescription = ({ orderLine, }) => {
    var _a;
    // PHP sends template: null when the template instance can no longer be
    // loaded, so check the value and not just the key.
    if ("template" in orderLine && orderLine.template) {
        return React.createElement(TemplateName, { uuid: orderLine.template.uuid });
    }
    else if ("catalogue" in orderLine && orderLine.catalogue) {
        return React.createElement(React.Fragment, null, orderLine.catalogue.name);
    }
    else if (orderLine.product.uuid == PRODUCT_DIGITAL_SET_UUID &&
        "subjects" in orderLine) {
        return (React.createElement(React.Fragment, null, (_a = orderLine.subjects) === null || _a === void 0 ? void 0 : _a.map((subject) => subject.name).join(", ")));
    }
    return null;
};
const DisplayOrderSummary = ({ orderLine, }) => {
    const classes = useStyles();
    return (React.createElement(Box, { display: "flex", justifyContent: "space-between" },
        React.createElement("div", null, orderLine.description),
        React.createElement("div", { className: classes.priceToPay }, formatCurrency(orderLine.linePrice))));
};
export const SubProducts = ({ productUuid, subProductUuids, }) => {
    var _a;
    const { getProductByUuid } = usePriceList();
    const classes = useStyles();
    const product = useMemo(() => {
        return getProductByUuid(productUuid);
    }, [getProductByUuid, productUuid]);
    if (subProductUuids.length == 0) {
        return null;
    }
    const subProducts = (_a = product === null || product === void 0 ? void 0 : product.subproducts) !== null && _a !== void 0 ? _a : [];
    const nameParts = subProductUuids.flatMap((uuid) => subProducts
        .filter((subProduct) => subProduct.uuid === uuid)
        .map((subProduct) => subProduct.name));
    return React.createElement("div", { className: classes.subProductName }, nameParts.join(" • "));
};
export default OrderContent;
